/**
 * GeneralResultCard - Structured display of completed general research results.
 *
 * Comparable to FinancialResultCard and StrategicResultCard, this component
 * renders general research with clear visual hierarchy:
 * - Header with confidence badge
 * - Executive Summary
 * - Key Findings (per-task cards with confidence + sources)
 * - Debate & Dissent (unified section, extracted from per-task rendering)
 * - Sources (grouped by task with authority badges)
 * - Footer with export + new research
 */

import { useState } from 'react';
import { cn } from '@/lib/utils';
import {
  Scale,
  ChevronDown,
  ChevronUp,
  Clock,
  Sparkles,
  RefreshCw,
  ShieldCheck,
  AlertTriangle,
  CheckCircle,
  MessageSquare,
  Bot,
  FileText,
  ExternalLink,
  Microscope,
  Eye,
  Shield,
} from 'lucide-react';
import type { ResearchSession, TaskResult, DebateState, SourceInfo } from '@/hooks/useResearchSession';
import { MarkdownContent } from './MarkdownContent';
import { ExportMenu } from './ExportMenu';
import type { ExportFormat } from '@/types/researchModes';
import { getConfidenceDisplay, getConfidenceHint } from '@/utils/confidenceUtils';
import { getSourceBadge, getHostname } from '@/utils/sourceUtils';
import { getProviderFromModel, getProviderLogo, getProviderColor as getProviderColorUtil } from '@/utils/providerUtils';

interface GeneralResultCardProps {
  session: ResearchSession;
  query: string;
  onNewResearch?: () => void;
  onExport?: (format: ExportFormat) => Promise<void>;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  className?: string;
}

export function GeneralResultCard({
  session,
  query,
  onNewResearch,
  onExport,
  isCollapsed = false,
  onToggleCollapse,
  className,
}: GeneralResultCardProps) {
  const [expandedFindings, setExpandedFindings] = useState<Set<number>>(new Set());
  const [expandedDebates, setExpandedDebates] = useState<Set<number>>(new Set());
  const [queryExpanded, setQueryExpanded] = useState(false);

  const reportData = session.reportData;
  if (!reportData) return null;

  const toggleFinding = (index: number) => {
    setExpandedFindings((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleDebate = (taskIndex: number) => {
    setExpandedDebates((prev) => {
      const next = new Set(prev);
      if (next.has(taskIndex)) next.delete(taskIndex);
      else next.add(taskIndex);
      return next;
    });
  };

  // Calculate overall confidence
  const overallConfidence = reportData.confidenceScore ??
    (reportData.taskResults.length > 0
      ? reportData.taskResults.reduce((sum, t) => sum + (t.confidenceScore ?? 0), 0) / reportData.taskResults.length
      : undefined);

  const confidenceDisplay = overallConfidence !== undefined ? getConfidenceDisplay(overallConfidence) : null;

  // Collect all debates
  const debatesPerTask = session.debatesPerTask;
  const hasDebates = debatesPerTask && debatesPerTask.size > 0;
  const hasStressTest = hasDebates && Array.from(debatesPerTask.values()).some(d => d.debateMode === 'stress_test');

  // Collect challenge points from fact-check progress (available even without debate)
  const factCheckProgress = session.factCheckProgress;
  const allChallengePoints: string[] = [];
  if (factCheckProgress) {
    for (const [, fcp] of factCheckProgress) {
      if (fcp.challengePoints) {
        allChallengePoints.push(...fcp.challengePoints);
      }
    }
  }

  // Collect watch items from debates
  const allWatchItems: string[] = [];
  if (hasDebates) {
    for (const [, debate] of debatesPerTask!) {
      if (debate.watchItems?.length) {
        allWatchItems.push(...debate.watchItems);
      }
    }
  }

  // Compute adversarial summary for Executive Summary
  const debateCount = debatesPerTask?.size ?? 0;
  const totalChallengesTested = hasDebates
    ? Array.from(debatesPerTask!.values()).reduce((sum, d) => sum + (d.challengesTested?.length ?? 0), 0)
    : 0;
  const avgAgreement = hasDebates
    ? Array.from(debatesPerTask!.values()).reduce((sum, d) => sum + d.consensusProgress, 0) / debateCount
    : 0;

  // Collect all sources grouped by task
  const sourcesByTask: Array<{ taskName: string; sources: Array<{ url: string; title: string }> }> = [];
  for (const task of reportData.taskResults) {
    if (task.webSources && task.webSources.length > 0) {
      sourcesByTask.push({ taskName: task.taskName, sources: task.webSources });
    }
  }
  // Fallback: if no per-task sources, use top-level sources
  const allSources = reportData.sources;

  const formatDuration = (seconds?: number) => {
    if (!seconds) return null;
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const mins = Math.floor(seconds / 60);
    const secs = Math.round(seconds % 60);
    return `${mins}m ${secs}s`;
  };

  // Collapsed view — compact summary row matching other result cards
  if (isCollapsed) {
    return (
      <div
        className={cn(
          'relative w-full bg-surface border border-verdict-primary/20 overflow-hidden rounded-xl transition-all duration-300',
          className,
        )}
      >
        <div className="p-3 sm:p-4 flex items-center gap-3">
          {/* Icon */}
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-verdict-primary/20 flex items-center justify-center">
            <Scale className="w-4 h-4 text-verdict-primary" />
          </div>

          {/* Query + meta */}
          <div className="flex-1 min-w-0">
            <button
              onClick={(e) => { e.stopPropagation(); setQueryExpanded(!queryExpanded); }}
              className="text-sm text-text-primary font-medium text-left w-full cursor-pointer"
            >
              <span className={queryExpanded ? undefined : 'line-clamp-1'}>{query}</span>
            </button>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-xs text-text-muted">General Research</span>
              {reportData.durationSeconds && (
                <span className="text-xs text-text-muted">&middot; {formatDuration(reportData.durationSeconds)}</span>
              )}
              {reportData.taskResults.length > 0 && (
                <span className="text-xs text-text-muted">&middot; {reportData.taskResults.length} tasks</span>
              )}
            </div>
          </div>

          {/* Confidence badge */}
          {confidenceDisplay && overallConfidence !== undefined && (
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-1 rounded-full border flex-shrink-0',
              confidenceDisplay.bgColor
            )}>
              {confidenceDisplay.icon === 'shield' && <ShieldCheck className={cn('w-3 h-3', confidenceDisplay.color)} />}
              {confidenceDisplay.icon === 'check' && <CheckCircle className={cn('w-3 h-3', confidenceDisplay.color)} />}
              {confidenceDisplay.icon === 'warning' && <AlertTriangle className={cn('w-3 h-3', confidenceDisplay.color)} />}
              <span className={cn('text-xs font-semibold', confidenceDisplay.color)}>
                {Math.round(overallConfidence * 100)}%
              </span>
            </div>
          )}

          {/* Stress test badge */}
          {hasStressTest && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex-shrink-0">
              <Microscope className="w-3 h-3 text-cyan-400" />
              <span className="text-[10px] text-cyan-400 font-medium hidden sm:inline">Tested</span>
            </div>
          )}

          {/* Expand button */}
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center gap-1 px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors flex-shrink-0"
            >
              <ChevronDown className="w-4 h-4" />
              <span className="hidden sm:inline">Details</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col h-full overflow-hidden', className)}>
      {/* === HEADER === */}
      <div className="flex-shrink-0 mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-verdict-primary">
            <Scale className="w-5 h-5" />
            <span className="text-xs font-mono uppercase tracking-wider">Research Complete</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Confidence Badge */}
            {confidenceDisplay && (
              <div className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-full border',
                confidenceDisplay.bgColor
              )}>
                {confidenceDisplay.icon === 'shield' && <ShieldCheck className={cn('w-4 h-4', confidenceDisplay.color)} />}
                {confidenceDisplay.icon === 'check' && <CheckCircle className={cn('w-4 h-4', confidenceDisplay.color)} />}
                {confidenceDisplay.icon === 'warning' && <AlertTriangle className={cn('w-4 h-4', confidenceDisplay.color)} />}
                <span className={cn('text-xs font-semibold', confidenceDisplay.color)}>
                  {Math.round(overallConfidence! * 100)}%
                </span>
                <span className={cn('text-[10px] uppercase tracking-wider', confidenceDisplay.color)}>
                  {confidenceDisplay.label}
                </span>
              </div>
            )}
            {/* Stress Test Badge */}
            {hasStressTest && (
              <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/30">
                <Microscope className="w-3 h-3 text-cyan-400" />
                <span className="text-[10px] text-cyan-400 font-medium">Stress-Tested</span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => setQueryExpanded(!queryExpanded)}
          className="text-text-primary/80 text-sm text-left cursor-pointer"
        >
          <span className={queryExpanded ? undefined : 'line-clamp-2'}>&ldquo;{query}&rdquo;</span>
        </button>
        <div className="flex items-center gap-3 mt-2 text-xs">
          {reportData.durationSeconds && (
            <div className="flex items-center gap-1.5 text-text-muted">
              <Clock className="w-3.5 h-3.5" />
              <span>Completed in {formatDuration(reportData.durationSeconds)}</span>
            </div>
          )}
          {hasDebates && (
            <div className="flex items-center gap-1.5 text-orange-400">
              <MessageSquare className="w-3.5 h-3.5" />
              <span>{debatesPerTask!.size} {debatesPerTask!.size === 1 ? 'debate' : 'debates'} resolved</span>
            </div>
          )}
        </div>
      </div>

      {/* === SCROLLABLE CONTENT === */}
      <div className="flex-grow overflow-y-auto pr-2 space-y-4 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
        {/* --- Executive Summary --- */}
        <div className="verdict-card p-4 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-verdict-primary" />
            <h3 className="text-sm font-semibold text-text-primary">Executive Summary</h3>
          </div>
          <MarkdownContent content={reportData.verdict} />
          {overallConfidence !== undefined && (
            <div className="mt-4 p-3 rounded-lg bg-surface-light/50 border border-border/50">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-text-secondary">Fact-Check Confidence</span>
                <span className={cn('text-xs font-semibold', confidenceDisplay?.color || 'text-text-secondary')}>
                  {Math.round(overallConfidence * 100)}%
                </span>
              </div>
              <div className="h-2 bg-surface-raised rounded-full overflow-hidden">
                <div
                  className={cn(
                    'h-full rounded-full transition-all duration-500',
                    overallConfidence >= 0.85 ? 'bg-emerald-400' :
                    overallConfidence >= 0.7 ? 'bg-verdict-primary' :
                    overallConfidence >= 0.5 ? 'bg-amber-400' : 'bg-red-400'
                  )}
                  style={{ width: `${overallConfidence * 100}%` }}
                />
              </div>
              <p className="text-[10px] text-text-muted mt-2">
                {getConfidenceHint(overallConfidence)}
              </p>
              {/* Adversarial summary line */}
              {hasDebates && totalChallengesTested > 0 ? (
                <p className="text-[10px] text-cyan-400/80 mt-1 flex items-center gap-1">
                  <Microscope className="w-3 h-3 inline" />
                  {hasStressTest && !Array.from(debatesPerTask!.values()).some(d => d.debateMode === 'full')
                    ? `Stress-tested against ${totalChallengesTested} challenge point${totalChallengesTested !== 1 ? 's' : ''} — conclusion held at ${Math.round(avgAgreement * 100)}% agreement`
                    : `Debated across ${debateCount} task${debateCount !== 1 ? 's' : ''} — resolved at ${Math.round(avgAgreement * 100)}% agreement`
                  }
                </p>
              ) : allChallengePoints.length > 0 ? (
                <p className="text-[10px] text-cyan-400/80 mt-1 flex items-center gap-1">
                  <Shield className="w-3 h-3 inline" />
                  {allChallengePoints.length} assumption{allChallengePoints.length !== 1 ? 's' : ''} identified for monitoring
                </p>
              ) : null}
            </div>
          )}
        </div>

        {/* --- Key Findings --- */}
        {reportData.taskResults.length > 0 && (
          <div className="space-y-2">
            <h3 className="text-xs font-mono uppercase tracking-wider text-text-muted mb-2">
              Key Findings ({reportData.taskResults.length} tasks)
            </h3>
            {reportData.taskResults.map((task, index) => (
              <FindingCard
                key={index}
                task={task}
                index={index}
                isExpanded={expandedFindings.has(index)}
                onToggle={() => toggleFinding(index)}
              />
            ))}
          </div>
        )}

        {/* --- Adversarial Analysis (Always Visible) --- */}
        {hasDebates ? (
          <DebateDissent
            debatesPerTask={debatesPerTask!}
            taskResults={reportData.taskResults}
            expandedDebates={expandedDebates}
            onToggleDebate={toggleDebate}
          />
        ) : allChallengePoints.length > 0 ? (
          <ChallengeReport challengePoints={allChallengePoints} />
        ) : (
          <div className="verdict-card p-3 rounded-lg">
            <div className="flex items-center gap-2 text-text-muted">
              <Shield className="w-4 h-4 text-cyan-400/60" />
              <span className="text-xs">Cross-validated by {reportData.taskResults.length > 0 ? reportData.taskResults[0]?.agentOutputs?.length ?? 3 : 3} independent agents.</span>
            </div>
          </div>
        )}

        {/* --- Watch Items --- */}
        {allWatchItems.length > 0 && (
          <WatchItems items={allWatchItems} />
        )}

        {/* --- Sources --- */}
        {allSources.length > 0 && (
          <GroupedSourcesList
            sources={allSources}
          />
        )}
      </div>

      {/* === FOOTER === */}
      <div className="flex-shrink-0 pt-4 mt-auto border-t border-border-subtle">
        <div className="flex gap-2">
          {onToggleCollapse && (
            <button
              onClick={onToggleCollapse}
              className="flex items-center gap-1 px-3 py-2.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface-light rounded-lg transition-colors"
            >
              <ChevronUp className="w-4 h-4" />
              <span className="hidden sm:inline">Collapse</span>
            </button>
          )}
          {reportData.s3Url && (
            <a
              href={reportData.s3Url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-verdict-primary/20 hover:bg-verdict-primary/30 border border-verdict-primary/40 rounded-lg text-verdict-primary text-sm font-medium transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              View Full Report
            </a>
          )}
          {onExport && (
            <ExportMenu
              onExport={onExport}
              availableFormats={['pdf', 'markdown']}
            />
          )}
          {onNewResearch && (
            <button
              onClick={onNewResearch}
              className="flex items-center justify-center gap-2 px-4 py-2.5 bg-surface-light hover:bg-surface-raised border border-border rounded-lg text-text-primary text-sm font-medium transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              New
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// === SUB-COMPONENTS ===

/** Finding Card - Per-task result with confidence + sources */
function FindingCard({
  task,
  index,
  isExpanded,
  onToggle,
}: {
  task: TaskResult;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const taskConfidence = task.confidenceScore !== undefined ? getConfidenceDisplay(task.confidenceScore) : null;

  return (
    <div className="verdict-card rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full p-3 flex items-center justify-between hover:bg-surface-light transition-colors text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-shrink-0 w-6 h-6 rounded-full bg-verdict-primary/20 flex items-center justify-center">
            <span className="text-xs font-mono text-verdict-primary">{index + 1}</span>
          </div>
          <div className="min-w-0">
            <h4 className="text-sm font-medium text-text-primary truncate">{task.taskName}</h4>
            {task.taskDescription && (
              <p className="text-xs text-text-muted truncate mt-0.5">{task.taskDescription}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 ml-2">
          {task.confidenceScore !== undefined && taskConfidence && (
            <div className={cn(
              'flex items-center gap-1.5 px-2 py-0.5 rounded-full border',
              taskConfidence.bgColor
            )}>
              {taskConfidence.icon === 'shield' && <ShieldCheck className={cn('w-3 h-3', taskConfidence.color)} />}
              {taskConfidence.icon === 'check' && <CheckCircle className={cn('w-3 h-3', taskConfidence.color)} />}
              {taskConfidence.icon === 'warning' && <AlertTriangle className={cn('w-3 h-3', taskConfidence.color)} />}
              <span className={cn('text-xs font-mono', taskConfidence.color)}>
                {Math.round(task.confidenceScore * 100)}%
              </span>
            </div>
          )}
          {task.agentOutputs.length > 0 && (
            <span className="text-xs text-text-muted">
              {task.agentOutputs.length} agent{task.agentOutputs.length !== 1 ? 's' : ''}
            </span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-text-muted" />
          ) : (
            <ChevronDown className="w-4 h-4 text-text-muted" />
          )}
        </div>
      </button>

      {/* Collapsed: Show consensus summary + inline sources */}
      {!isExpanded && task.aggregatedOutput && (
        <div className="px-3 pb-3">
          <div className="text-[13px] text-text-secondary line-clamp-3">
            <MarkdownContent content={task.aggregatedOutput} className="text-[13px]" />
          </div>
          {task.webSources && task.webSources.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {task.webSources.slice(0, 4).map((src, idx) => (
                <a
                  key={idx}
                  href={src.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-text-muted hover:text-verdict-primary underline truncate max-w-[140px]"
                >
                  {getHostname(src.url)}
                </a>
              ))}
              {task.webSources.length > 4 && (
                <span className="text-[10px] text-text-muted">+{task.webSources.length - 4} more</span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Expanded: Full agent outputs */}
      {isExpanded && (
        <div className="border-t border-border-subtle">
          {task.aggregatedOutput && (
            <div className="p-3 border-b border-border-subtle bg-verdict-primary/5">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-3.5 h-3.5 text-verdict-primary" />
                <span className="text-xs font-semibold text-verdict-primary uppercase tracking-wider">
                  Consensus
                </span>
              </div>
              <MarkdownContent content={task.aggregatedOutput} className="text-[15px]" />
            </div>
          )}
          {task.agentOutputs.length > 0 && (
            <div className="divide-y divide-white/5">
              {task.agentOutputs.map((agent, agentIdx) => {
                const provider = getProviderFromModel(agent.modelName);
                return (
                  <div key={agentIdx} className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Bot className="w-3.5 h-3.5" style={{ color: getProviderColorUtil(provider, 'hex') }} />
                      <span
                        className="text-[13px] font-medium"
                        style={{ color: getProviderColorUtil(provider, 'hex') }}
                      >
                        {agent.agentName}
                      </span>
                      <span className="text-[13px] text-text-muted font-mono">{agent.modelName}</span>
                    </div>
                    <MarkdownContent content={agent.output} className="text-[15px] text-text-secondary" />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Debate & Dissent - Unified section for all debates */
function DebateDissent({
  debatesPerTask,
  taskResults,
  expandedDebates,
  onToggleDebate,
}: {
  debatesPerTask: Map<number, DebateState>;
  taskResults: TaskResult[];
  expandedDebates: Set<number>;
  onToggleDebate: (taskIndex: number) => void;
}) {
  const debates = Array.from(debatesPerTask.entries());
  const stressTestCount = debates.filter(([, d]) => d.debateMode === 'stress_test').length;
  const fullDebateCount = debates.filter(([, d]) => d.debateMode !== 'stress_test').length;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-mono uppercase tracking-wider text-text-muted flex items-center gap-2">
          <MessageSquare className="w-3.5 h-3.5 text-orange-400" />
          Debate & Dissent ({debates.length})
        </h3>
        <div className="flex items-center gap-2">
          {stressTestCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
              {stressTestCount} stress test{stressTestCount > 1 ? 's' : ''}
            </span>
          )}
          {fullDebateCount > 0 && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400">
              {fullDebateCount} full debate{fullDebateCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      {debates.map(([taskIndex, debate]) => {
        const taskName = taskResults[taskIndex]?.taskName || `Task ${taskIndex + 1}`;
        const isExpanded = expandedDebates.has(taskIndex);
        const isStressTest = debate.debateMode === 'stress_test';
        const consensusPercent = Math.round(debate.consensusProgress * 100);

        return (
          <div
            key={taskIndex}
            className={cn(
              'verdict-card rounded-lg overflow-hidden border',
              isStressTest ? 'border-cyan-500/20' : 'border-orange-500/20'
            )}
          >
            {/* Debate Summary Header */}
            <button
              onClick={() => onToggleDebate(taskIndex)}
              className="w-full p-3 flex items-center justify-between hover:bg-surface-light transition-colors text-left"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={cn(
                  'flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center',
                  isStressTest ? 'bg-cyan-500/20' : 'bg-orange-500/20'
                )}>
                  {isStressTest
                    ? <Microscope className="w-4 h-4 text-cyan-400" />
                    : <MessageSquare className="w-4 h-4 text-orange-400" />
                  }
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium text-text-primary truncate">
                    {taskName}
                  </h4>
                  <p className="text-xs text-text-muted mt-0.5">
                    {isStressTest ? 'Stress test' : 'Full debate'} &middot; {debate.arguments.length} arguments &middot; {debate.maxRounds} round{debate.maxRounds !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                <span className={cn(
                  'text-xs font-mono px-2 py-0.5 rounded-full',
                  consensusPercent >= 70 ? 'bg-emerald-500/20 text-emerald-400' :
                  consensusPercent >= 50 ? 'bg-amber-500/20 text-amber-400' :
                  'bg-red-500/20 text-red-400'
                )}>
                  {consensusPercent}% agreement
                </span>
                {isExpanded ? (
                  <ChevronUp className="w-4 h-4 text-text-muted" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-text-muted" />
                )}
              </div>
            </button>

            {/* Collapsed: Key takeaway */}
            {!isExpanded && (
              <div className="px-3 pb-3">
                {debate.discrepancies.length > 0 && (
                  <p className="text-[13px] text-text-secondary line-clamp-2">
                    <span className="text-text-muted font-medium">Trigger: </span>
                    {debate.discrepancies[0]}
                  </p>
                )}
                {debate.finalConsensus && (
                  <div className="text-[13px] text-text-primary line-clamp-2 mt-1">
                    <span className={cn(
                      'font-medium',
                      consensusPercent >= 70 ? 'text-emerald-400' : 'text-amber-400'
                    )}>
                      Resolution:{' '}
                    </span>
                    <MarkdownContent content={debate.finalConsensus.slice(0, 200)} className="text-[13px] text-text-primary inline" inline />
                  </div>
                )}
              </div>
            )}

            {/* Expanded: Full transcript */}
            {isExpanded && (
              <div className="border-t border-border-subtle p-3 space-y-3 max-h-[400px] overflow-y-auto scrollbar-thin scrollbar-thumb-zinc-700">
                {/* Discrepancies */}
                {debate.discrepancies.length > 0 && (
                  <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <div className="flex items-center gap-2 text-xs text-amber-400 font-mono mb-1.5">
                      <AlertTriangle className="w-3.5 h-3.5" />
                      <span className="font-medium">
                        {isStressTest ? 'Assumptions Challenged:' : 'Discrepancies to Resolve:'}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {debate.discrepancies.map((d, idx) => (
                        <li key={idx} className="text-[13px] text-text-primary pl-2 border-l-2 border-amber-500/40">
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Mediator Opening */}
                {debate.mediatorOpening && (
                  <div className="p-2 rounded-lg bg-gradient-to-r from-cyan-500/15 to-cyan-600/5 border border-cyan-500/30">
                    <div className="flex items-center gap-2 text-xs text-cyan-400 font-mono mb-1.5">
                      <Scale className="w-3.5 h-3.5" />
                      <span className="font-medium">Moderator Opening</span>
                    </div>
                    <MarkdownContent content={debate.mediatorOpening} className="text-xs text-text-primary" />
                  </div>
                )}

                {/* Arguments grouped by round */}
                {(() => {
                  const roundGroups = new Map<number, typeof debate.arguments>();
                  for (const arg of debate.arguments) {
                    const r = arg.roundNumber;
                    if (!roundGroups.has(r)) roundGroups.set(r, []);
                    roundGroups.get(r)!.push(arg);
                  }
                  const sortedRounds = Array.from(roundGroups.entries()).sort(([a], [b]) => a - b);
                  return sortedRounds.map(([roundNum, args], groupIdx) => (
                    <div key={roundNum}>
                      {sortedRounds.length > 1 && (
                        <div className="flex items-center gap-2 my-2">
                          <div className="h-px flex-1 bg-surface-raised/60" />
                          <span className="text-[9px] font-mono text-text-muted uppercase tracking-wider">
                            {groupIdx === 0 ? 'Initial Positions' : `Round ${roundNum}`}
                          </span>
                          <div className="h-px flex-1 bg-surface-raised/60" />
                        </div>
                      )}
                      {args.map((arg, idx) => {
                        const provider = getProviderFromModel(arg.modelName);
                        const isLeft = idx % 2 === 0;
                        return (
                          <div
                            key={`${roundNum}-${idx}`}
                            className={cn('flex mb-2', isLeft ? 'justify-start' : 'justify-end')}
                          >
                            <div className={cn(
                              'max-w-[85%] p-2 rounded-lg border bg-gradient-to-br',
                              getProviderColorUtil(provider, 'gradient'),
                              isLeft ? 'rounded-tl-sm' : 'rounded-tr-sm'
                            )}>
                              <div className="flex items-center gap-1.5 mb-1">
                                <div className={cn('w-5 h-5 rounded-full flex items-center justify-center bg-surface-light', getProviderColorUtil(provider, 'text'))}>
                                  <span className="flex items-center justify-center">{getProviderLogo(provider, 14)}</span>
                                </div>
                                <span className={cn('text-[10px] font-mono font-medium', getProviderColorUtil(provider, 'text'))}>
                                  {arg.agentName}
                                </span>
                                <span className="text-[9px] text-text-muted ml-auto">
                                  R{arg.roundNumber}
                                </span>
                              </div>
                              <MarkdownContent content={arg.argument} className="text-[13px] text-text-primary" />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ));
                })()}

                {/* Final Consensus */}
                {debate.finalConsensus && (
                  <div className={cn(
                    'p-3 rounded-lg border',
                    consensusPercent >= 70
                      ? 'bg-gradient-to-r from-verdict-primary/20 to-amber-500/10 border-verdict-primary/40'
                      : 'bg-gradient-to-r from-amber-500/20 to-orange-500/10 border-amber-500/40'
                  )}>
                    <div className={cn(
                      'flex items-center gap-2 text-xs font-mono mb-2',
                      consensusPercent >= 70 ? 'text-verdict-primary' : 'text-amber-400'
                    )}>
                      <Scale className="w-4 h-4" />
                      <span className="font-bold uppercase tracking-wider">
                        {consensusPercent >= 70 ? 'Consensus Reached' : 'Final Assessment'}
                      </span>
                      <span className={cn(
                        'ml-auto px-2 py-0.5 rounded-full font-bold',
                        consensusPercent >= 70 ? 'bg-emerald-500/20 text-emerald-400' :
                        consensusPercent >= 50 ? 'bg-amber-500/20 text-amber-400' :
                        'bg-red-500/20 text-red-400'
                      )}>
                        {consensusPercent}%
                      </span>
                    </div>
                    <MarkdownContent content={debate.finalConsensus} className="text-[15px] text-text-primary" />
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/** Challenge Report — shown when fact-checker identified challenge points but no full debate ran */
function ChallengeReport({ challengePoints }: { challengePoints: string[] }) {
  return (
    <div className="verdict-card p-3 rounded-lg border border-cyan-500/20">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-cyan-400" />
        <span className="text-xs font-mono uppercase tracking-wider text-cyan-400/80">
          Assumptions Identified ({challengePoints.length})
        </span>
      </div>
      <p className="text-[10px] text-text-muted mb-2">
        These assumptions were flagged during cross-validation. The conclusion depends on them holding true.
      </p>
      <ul className="space-y-1.5">
        {challengePoints.map((point, idx) => (
          <li key={idx} className="text-[13px] text-text-primary pl-2 border-l-2 border-cyan-500/40 leading-relaxed">
            {point}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Watch Items — conditions under which the conclusion should be revisited */
function WatchItems({ items }: { items: string[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayItems = isExpanded ? items : items.slice(0, 3);

  return (
    <div className="verdict-card p-3 rounded-lg border border-amber-500/15">
      <button
        onClick={() => items.length > 3 && setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between mb-2"
      >
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4 text-amber-400/80" />
          <span className="text-xs font-mono uppercase tracking-wider text-amber-400/80">
            Watch Items ({items.length})
          </span>
        </div>
        {items.length > 3 && (
          <span className="text-[10px] text-text-muted hover:text-text-primary transition-colors">
            {isExpanded ? 'Show less' : 'Show all'}
          </span>
        )}
      </button>
      <p className="text-[10px] text-text-muted mb-2">
        Conditions that could change this conclusion. Monitor these for updates.
      </p>
      <ul className="space-y-1.5">
        {displayItems.map((item, idx) => (
          <li key={idx} className="text-[13px] text-text-secondary pl-2 border-l-2 border-amber-500/30 leading-relaxed">
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Grouped Sources List - Sources organized by task with authority badges */
function GroupedSourcesList({
  sources,
}: {
  sources: SourceInfo[];
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Count authoritative sources
  const authCounts = {
    gov: sources.filter(s => getSourceBadge(s.url).type === 'government').length,
    edu: sources.filter(s => getSourceBadge(s.url).type === 'academic').length,
    research: sources.filter(s => getSourceBadge(s.url).type === 'research').length,
    news: sources.filter(s => getSourceBadge(s.url).type === 'news').length,
  };
  const hasAuthoritativeSources = authCounts.gov + authCounts.edu + authCounts.research > 0;

  const displaySources = isExpanded ? sources : sources.slice(0, 6);

  return (
    <div className="verdict-card p-3 rounded-lg">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-text-muted" />
          <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
            Sources ({sources.length})
          </span>
        </div>
        {sources.length > 6 && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-xs text-text-muted hover:text-text-primary transition-colors"
          >
            {isExpanded ? 'Show less' : `Show all ${sources.length}`}
          </button>
        )}
      </div>

      {/* Authority summary badges */}
      {hasAuthoritativeSources && (
        <div className="flex flex-wrap gap-2 mb-3">
          {authCounts.gov > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-400/10 border border-blue-400/30">
              <ShieldCheck className="w-3 h-3 text-blue-400" />
              <span className="text-[10px] text-blue-400 font-medium">{authCounts.gov} Gov</span>
            </div>
          )}
          {authCounts.edu > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-400/10 border border-purple-400/30">
              <CheckCircle className="w-3 h-3 text-purple-400" />
              <span className="text-[10px] text-purple-400 font-medium">{authCounts.edu} Edu</span>
            </div>
          )}
          {authCounts.research > 0 && (
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-400/10 border border-emerald-400/30">
              <CheckCircle className="w-3 h-3 text-emerald-400" />
              <span className="text-[10px] text-emerald-400 font-medium">{authCounts.research} Research</span>
            </div>
          )}
        </div>
      )}

      {/* Source list */}
      <div className="space-y-2">
        {displaySources.map((source, idx) => {
          const badge = getSourceBadge(source.url);
          return (
            <a
              key={idx}
              href={source.url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col gap-0.5 text-xs text-text-muted hover:text-verdict-primary transition-colors group"
            >
              <div className="flex items-center gap-2">
                {badge.type !== 'standard' ? (
                  <div className={cn('flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border', badge.bgColor)}>
                    {badge.type === 'government' && <ShieldCheck className={cn('w-2.5 h-2.5', badge.color)} />}
                    {(badge.type === 'academic' || badge.type === 'research') && <CheckCircle className={cn('w-2.5 h-2.5', badge.color)} />}
                    {badge.type === 'news' && <FileText className={cn('w-2.5 h-2.5', badge.color)} />}
                    <span className={badge.color}>{badge.label}</span>
                  </div>
                ) : (
                  <ExternalLink className="w-3 h-3 flex-shrink-0 opacity-50 group-hover:opacity-100" />
                )}
                <span className="truncate font-medium text-text-primary group-hover:text-verdict-primary">
                  {source.title || getHostname(source.url)}
                </span>
              </div>
              <span className="text-[11px] text-text-muted truncate ml-5">{getHostname(source.url)}</span>
            </a>
          );
        })}
      </div>
    </div>
  );
}
